import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import {
  ChangeSummaryGroupVm,
  ClientAcceptanceVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { formatSignedMoneyList } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { formatDateTime } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { pluralizeItems } from '@/features/dashboard/bookingChanges/utils/plurals.ts';

/**
 * Napisy kroku W3 — strony spod linku z maila i paneli objaśniających ją
 * operatorowi.
 *
 * Karty dostają zdania gotowe, więc żadna nie skleja ich z danych sama.
 */

/** Przyciski klienta są w tym kroku wyłącznie podglądem — stąd wspólny dopisek. */
export const PREVIEW_ONLY_TITLE =
  'To podgląd strony zapisującego — decyzję podejmuje on, z linku w mailu.';

/** Wstęp strony klienta: czego dotyczą zmiany i jak czytać to, co niżej. */
export const acceptanceIntroSentence = (view: ClientAcceptanceVm): string => {
  const scope = view.scopeLabel
    ? `dla ${view.scopeLabel}`
    : 'w Twojej rezerwacji';
  return `${view.tourName}, ${view.dateRangeLabel}. Organizator przygotował zmiany ${scope}. Poniżej dokładnie to, co się zmienia — pozycja po pozycji, w wartościach sprzed i po zmianie.`;
};

/**
 * Nagłówek belki, która u operatora wchodzi na miejsce paska szkicu. Paczkę
 * identyfikuje rezerwacja, a nie numer: numer wynika z pozycji na liście paczek
 * rezerwacji, a strona akceptacji stoi na tokenie jednej paczki i takiej listy
 * nie ma.
 */
export const acceptancePackageHeading = (bookingCode: string): string =>
  `Paczka w rezerwacji ${bookingCode} czeka na akceptację`;

/**
 * Dopisek tej belki. Pozycje liczy się tutaj, bo liczba pada wyłącznie w tym
 * jednym zdaniu.
 */
export const acceptancePackageMetaLabel = (
  sentAt: string | null,
  groups: ChangeSummaryGroupVm[],
  balance: MoneyValueDto[],
): string => {
  const itemCount = groups.reduce(
    (count, group) => count + group.items.length,
    0,
  );

  return [
    sentAt && `wysłana ${formatDateTime(sentAt)}`,
    pluralizeItems(itemCount),
    formatSignedMoneyList(balance),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Dopisek pod przyciskami decyzji.
 *
 * Terminu ważności linku nie podajemy, bo go nie ma: paczka czekająca na
 * klienta nie wygasa sama, a token rezerwacji żyje do końca wyjazdu.
 */
export const ACCEPTANCE_LINK_NOTE =
  'Link działa, dopóki organizator nie zdejmie paczki.';

/** Ostatnia pozycja listy „Po decyzji” — co się dzieje, gdy klient milczy. */
export const ACCEPTANCE_NO_RESPONSE_SENTENCE =
  'zostawia paczkę w oczekiwaniu — nic nie wygasa samo, a zdjąć ją może wyłącznie organizator.';
